import React, { Fragment } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme, Breakpoint } from '../../../theme';
import { defaultLabelId, joinIds } from '../idHelpers';
import { FieldSetInputProps, InputComponent } from '../inputTypes';
import {
  ContentAlignment,
  Description,
  HiddenInput,
  Orientation,
  RadioItem,
  RadioTextWrapper,
  StyledIcon,
  Wrapper,
} from './styles';

const MOBILE_HORIZONTAL_CHARACTER_LIMIT = 4;

export type ButtonsPerRowByBreakpoint = {
  [T in Breakpoint]?: number;
};

export type Option = {
  name: string;
  value: string;
  icon?: string;
  description?: string;
};

/**
 * @param {string} id: Id used for wrapper and prefixed to id of each option.
 * @param {string} value: Currently selected option of radio input (must match value of one of the options).
 * @param {Option[]} options: Array of radio input options, each with 'name' and 'value'.
 * @param {(event) => void} onChange: Callback to invoke on selection of radio input value.
 * @param {Orientation} orientationOverride: Fixes orientation of options to {Orientation} provided.
 *    (!) Use carefully - default behaviour ensures options display sensibly by switching orientation from horizontal to:
 *      (1) 'vertical' based on number of options
 *      (2) 'mobileVertical' based on option text length.
 * @param {boolean} disabled: Disables radio input.
 * @param {boolean} wrapItems: Controls the flex-wrap styling. Defaults to true with flex-wrap: wrap.
 * @param {ContentAlignment} contentAlignmentOverride: fixes aligment of content to {ContentAlignment} provided.
 * @param {ButtonsPerRowByBreakpoint} buttonsPerRow: how many buttons should be in a row before wrapping, by breakpoint - used only with horizontal alignment
 */
export type RadioInputProps = {
  id: string;
  value?: string;
  options: Option[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  orientationOverride?: Orientation;
  disabled?: boolean;
  wrapItems?: boolean;
  contentAlignmentOverride?: ContentAlignment;
  buttonsPerRow?: ButtonsPerRowByBreakpoint;
} & FieldSetInputProps &
  ComponentProps;

const getOrientation = (options: Option[]): Orientation => {
  if (options.length >= 3) {
    return Orientation.vertical;
  }
  if (options.some((option) => option.name.length > MOBILE_HORIZONTAL_CHARACTER_LIMIT)) {
    return Orientation.mobileVertical;
  }
  return Orientation.horizontal;
};

const getContentAlignment = (options: Option[]): ContentAlignment => {
  return options.some((option) => option.icon)
    ? ContentAlignment.left
    : ContentAlignment.center;
};

const RadioInput: InputComponent<RadioInputProps> = ({
  id,
  value,
  options,
  onChange,
  orientationOverride,
  disabled,
  wrapItems = true,
  contentAlignmentOverride,
  labelId = defaultLabelId(id),
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  buttonsPerRow,
  ...rest
}) => {
  const getIdForOption = (option: Option): string => `${id}-${option.value}`;
  const getLabelIdForOption = (option: Option): string =>
    defaultLabelId(getIdForOption(option));
  const orientation = orientationOverride ?? getOrientation(options);
  const contentAlignment = contentAlignmentOverride ?? getContentAlignment(options);

  return (
    <Wrapper
      {...componentProps(rest)}
      id={id}
      orientation={orientation}
      wrapItems={wrapItems}
      role="radiogroup"
      buttonsPerRow={buttonsPerRow}>
      {options.map((option, index) => (
        <Fragment key={option.value}>
          <HiddenInput
            id={getIdForOption(option)}
            name={id}
            type="radio"
            value={option.value}
            checked={option.value === value}
            onChange={onChange}
            aria-labelledby={joinIds(labelId, getLabelIdForOption(option))}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            disabled={disabled}
            // This needed so only the first item can be selected by tab when none are chosen
            tabIndex={!value && index !== 0 ? -1 : undefined}
          />
          <RadioItem
            id={getLabelIdForOption(option)}
            htmlFor={getIdForOption(option)}
            orientation={orientation}
            disabled={disabled}
            contentAlignment={contentAlignment}
            buttonsPerRow={buttonsPerRow}>
            {option.icon && <StyledIcon name={option.icon} size="xxx-large" />}
            <RadioTextWrapper>
              {option.name}
              {option.description && <Description> {option.description}</Description>}
            </RadioTextWrapper>
          </RadioItem>
        </Fragment>
      ))}
    </Wrapper>
  );
};

RadioInput.isFieldSet = true;

export default applyComponentTheme(RadioInput, 'RadioInput');
